import React, { useState, useCallback, useEffect, useRef } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  Form,
  Button,
  Alert,
  Spinner,
  Badge,
  Modal,
} from "react-bootstrap";
import {
  IconBrandWhatsapp,
  IconPlugConnected,
  IconQrcode,
  IconArrowLeft,
  IconCheck,
  IconAlertTriangle,
  IconClock,
  IconDeviceMobile,
} from "@tabler/icons-react";
import NumberFormat from "react-number-format";
import Swal from "sweetalert2";

const WhatsAppConnectionFlow = ({
  validatedData,
  onConnectionSuccess,
  onBack,
  isLoading,
  error,
  handleConnectionRequest,
  handleQrCodeGeneration,
  handleStatusCheck,
}) => {
  const [phoneNumber, setPhoneNumber] = useState("");
  const [isGeneratingQrCode, setIsGeneratingQrCode] = useState(false);
  const [connectionStep, setConnectionStep] = useState("phone_input"); // phone_input, instructions, qr_scan, completed
  const [connectionError, setConnectionError] = useState("");
  const [qrCodeData, setQrCodeData] = useState(null);
  const [countdown, setCountdown] = useState(0);
  const [isCheckingConnection, setIsCheckingConnection] = useState(false);
  const [isInitializingSession, setIsInitializingSession] = useState(false);
  const countdownInterval = useRef(null);
  const checkInterval = useRef(null);
  const sessionCheckInterval = useRef(null);
  const isMountedRef = useRef(true);

  // Limpar intervalos
  const clearIntervals = () => {
    if (countdownInterval.current) {
      clearInterval(countdownInterval.current);
      countdownInterval.current = null;
    }
    if (checkInterval.current) {
      clearInterval(checkInterval.current);
      checkInterval.current = null;
    }
    if (sessionCheckInterval.current) {
      clearInterval(sessionCheckInterval.current);
      sessionCheckInterval.current = null;
    }
    setIsCheckingConnection(false);
    setIsInitializingSession(false);
  };

  // Cleanup dos intervalos
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      clearIntervals();
    };
  }, []);

  // Função para avançar para instruções
  const handleProceedToInstructions = async () => {
    const cleanPhone = phoneNumber.replace(/\D/g, "");

    if (
      !phoneNumber ||
      (cleanPhone.length !== 10 && cleanPhone.length !== 11)
    ) {
      setConnectionError(
        "Por favor, insira um número de telefone válido (10 ou 11 dígitos)"
      );
      return;
    }

    setConnectionError("");
    setIsInitializingSession(true);

    try {
      // Mostrar loader do Swal
      Swal.fire({
        title: "Iniciando sessão WhatsApp...",
        text: "Aguarde enquanto preparamos a conexão",
        allowOutsideClick: false,
        showConfirmButton: false,
        didOpen: () => {
          Swal.showLoading();
        },
      });

      // Chamar handleConnectionRequest para iniciar a sessão
      const connectionResult = await handleConnectionRequest(phoneNumber);

      if (!connectionResult.success) {
        throw new Error(connectionResult.error || "Erro ao conectar sessão");
      }

      // Iniciar verificação de status a cada 2 segundos
      sessionCheckInterval.current = setInterval(async () => {
        if (!isMountedRef.current) {
          clearIntervals();
          return;
        }

        try {
          const statusResult = await handleStatusCheck(phoneNumber);

          if (!isMountedRef.current) return;

          if (
            statusResult.success &&
            statusResult.data?.status === "SCAN_QR_CODE"
          ) {
            // Limpar intervalo
            if (sessionCheckInterval.current) {
              clearInterval(sessionCheckInterval.current);
              sessionCheckInterval.current = null;
            }

            setIsInitializingSession(false);

            // Fechar Swal e ir para instruções
            Swal.close();
            setConnectionStep("instructions");
            return;
          }
        } catch (error) {
          console.error("Erro na verificação de status:", error);
        }
      }, 2000); // Verificar a cada 2 segundos

      // Timeout de segurança: parar após 30 segundos
      setTimeout(() => {
        if (sessionCheckInterval.current && isMountedRef.current) {
          if (sessionCheckInterval.current) {
            clearInterval(sessionCheckInterval.current);
            sessionCheckInterval.current = null;
          }

          setIsInitializingSession(false);
          Swal.close();
          setConnectionError("Tempo limite excedido. Tente novamente.");
        }
      }, 30000);
    } catch (error) {
      console.error("Erro ao iniciar sessão:", error);
      setIsInitializingSession(false);
      Swal.close();
      setConnectionError(
        error.message || "Erro ao iniciar sessão. Tente novamente."
      );
    }
  };

  // Função para gerar QR Code
  const handleGenerateQrCode = useCallback(async () => {
    setIsGeneratingQrCode(true);
    setConnectionError("");

    try {
      // Primeiro, conectar a sessão

      // Aguardar um pouco para a sessão estar pronta para QR
      await new Promise((resolve) => setTimeout(resolve, 2000));

      // Gerar QR Code
      const qrResult = await handleQrCodeGeneration(phoneNumber);

      if (qrResult.success) {
        setQrCodeData(qrResult.data);
        setConnectionStep("qr_scan");
        startCountdown();
      } else {
        throw new Error(qrResult.error || "Erro ao gerar QR Code");
      }
    } catch (error) {
      console.error("Erro ao iniciar conexão:", error);
      setConnectionError(
        error.message || "Erro ao iniciar conexão. Tente novamente."
      );
      setConnectionStep("instructions");
    } finally {
      setIsGeneratingQrCode(false);
    }
  }, [
    phoneNumber,
    validatedData,
    handleConnectionRequest,
    handleQrCodeGeneration,
  ]);

  // Callback para sucesso na conexão
  const handleConnectionSuccessInternal = useCallback(
    async (connectionData) => {
      try {
        // Chamar callback do componente pai
        if (onConnectionSuccess) {
          await onConnectionSuccess({
            ...connectionData,
            phoneNumber: phoneNumber,
            token: validatedData?.token,
          });
        }

        setConnectionStep("completed");
      } catch (error) {
        console.error("Erro ao finalizar conexão:", error);
        setConnectionError(
          "Erro ao finalizar conexão. Entre em contato com o suporte."
        );
      }
    },
    [phoneNumber, validatedData, onConnectionSuccess]
  );

  // Iniciar countdown de 30 segundos
  const startCountdown = () => {
    setCountdown(30);

    countdownInterval.current = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(countdownInterval.current);
          countdownInterval.current = null;
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    // Após 5 segundos, iniciar verificação de status
    setTimeout(() => {
      startConnectionChecking();
    }, 5000);
  };

  // Verificar conexão continuamente
  const startConnectionChecking = async () => {
    if (!isMountedRef.current) return;

    setIsCheckingConnection(true);

    checkInterval.current = setInterval(async () => {
      if (!isMountedRef.current) {
        clearIntervals();
        return;
      }

      try {
        const statusResult = await handleStatusCheck(phoneNumber);

        if (!isMountedRef.current) return;

        if (statusResult.success && statusResult.data?.status === "WORKING") {
          clearIntervals();
          handleConnectionSuccessInternal({
            phone: phoneNumber,
            status: statusResult.data.status,
            uid: statusResult.data.uid || null,
          });
          return;
        }
      } catch (error) {
        console.error("Erro na verificação periódica:", error);
      }
    }, 3000); // Verificar a cada 3 segundos

    // Timeout de segurança: parar após 25 segundos (deixar 5s de margem)
    setTimeout(() => {
      if (checkInterval.current && isMountedRef.current) {
        clearIntervals();
        setIsCheckingConnection(false);
        setConnectionError(
          "Tempo limite excedido. Tente gerar um novo QR Code."
        );
      }
    }, 25000);
  };

  // Função para voltar
  const handleBack = () => {
    clearIntervals();
    Swal.close(); // Fechar qualquer Swal aberto

    if (connectionStep === "qr_scan" || connectionStep === "instructions") {
      setConnectionStep("phone_input");
      setQrCodeData(null);
      setConnectionError("");
    } else {
      onBack();
    }
  };

  // Renderizar conteúdo baseado no passo
  const renderContent = () => {
    switch (connectionStep) {
      case "phone_input":
        return (
          <Card className="border-0 shadow-lg">
            <Card.Body className="p-5">
              {/* Header */}
              <div className="text-center mb-5">
                <div
                  className="mx-auto mb-4 rounded-circle d-flex align-items-center justify-content-center"
                  style={{
                    width: "80px",
                    height: "80px",
                    background:
                      "linear-gradient(135deg, #25D366 0%, #128C7E 100%)",
                    color: "white",
                    boxShadow: "0 8px 32px rgba(37, 211, 102, 0.3)",
                  }}
                >
                  <IconPlugConnected size={40} />
                </div>

                <h2 className="fw-bold text-dark mb-2">Conectar WhatsApp</h2>
                <p className="text-muted mb-0">
                  Digite o número do WhatsApp para conectar
                </p>

                {/* Informações da validação */}
                <div className="mt-4">
                  <Badge
                    bg="success"
                    className="d-flex align-items-center gap-2 px-3 py-2 mx-auto"
                    style={{ fontSize: "12px", width: "fit-content" }}
                  >
                    <IconCheck size={14} />
                    Token validado com sucesso
                  </Badge>
                </div>
              </div>

              {/* Formulário de Telefone */}
              <Form
                onSubmit={(e) => {
                  e.preventDefault();
                  handleProceedToInstructions();
                }}
              >
                <Form.Group className="mb-4">
                  <Form.Label className="fw-medium text-dark mb-2">
                    <IconBrandWhatsapp size={18} className="me-2" />
                    Número do WhatsApp
                  </Form.Label>
                  <NumberFormat
                    className="form-control"
                    format={(value) => {
                      const cleanValue = value.replace(/\D/g, "");
                      if (cleanValue.length === 0) return "";
                      if (cleanValue.length <= 2) {
                        return `(${cleanValue}`;
                      } else if (cleanValue.length <= 6) {
                        return cleanValue.replace(
                          /(\d{2})(\d{0,4})/,
                          "($1) $2"
                        );
                      } else if (cleanValue.length <= 10) {
                        return cleanValue.replace(
                          /(\d{2})(\d{4})(\d{0,4})/,
                          "($1) $2-$3"
                        );
                      } else {
                        return cleanValue.replace(
                          /(\d{2})(\d{5})(\d{0,4})/,
                          "($1) $2-$3"
                        );
                      }
                    }}
                    placeholder="(00) 00000-0000"
                    value={phoneNumber}
                    onValueChange={(values) => {
                      setPhoneNumber(values.formattedValue);
                      setConnectionError("");
                    }}
                    style={{
                      fontSize: "18px",
                      height: "56px",
                      borderRadius: "12px",
                      border: "2px solid #e9ecef",
                      textAlign: "center",
                    }}
                    disabled={isGeneratingQrCode || isInitializingSession}
                  />
                  <Form.Text className="text-muted">
                    Digite o número que será usado para conectar ao WhatsApp.
                    Aceita telefones fixos (10 dígitos) e celulares (11 dígitos)
                  </Form.Text>
                </Form.Group>

                {/* Erro */}
                {(connectionError || error) && (
                  <Alert variant="danger" className="mb-4">
                    {connectionError || error}
                  </Alert>
                )}

                {/* Botões */}
                <div className="d-flex gap-3">
                  <Button
                    variant="outline-secondary"
                    size="lg"
                    onClick={handleBack}
                    disabled={isGeneratingQrCode || isInitializingSession}
                    className="flex-fill"
                    style={{
                      borderRadius: "12px",
                      fontWeight: "600",
                      padding: "16px",
                    }}
                  >
                    <IconArrowLeft size={20} className="me-2" />
                    Voltar
                  </Button>

                  <Button
                    type="submit"
                    size="lg"
                    disabled={
                      isGeneratingQrCode ||
                      isInitializingSession ||
                      !phoneNumber ||
                      (phoneNumber.replace(/\D/g, "").length !== 10 &&
                        phoneNumber.replace(/\D/g, "").length !== 11)
                    }
                    className="flex-fill"
                    style={{
                      background:
                        "linear-gradient(135deg, #25D366 0%, #128C7E 100%)",
                      border: "none",
                      borderRadius: "12px",
                      fontWeight: "600",
                      padding: "16px",
                    }}
                  >
                    {isGeneratingQrCode || isInitializingSession ? (
                      <>
                        <Spinner
                          animation="border"
                          size="sm"
                          className="me-2"
                        />
                        {isInitializingSession
                          ? "Iniciando sessão..."
                          : "Iniciando..."}
                      </>
                    ) : (
                      <>
                        <IconArrowLeft size={20} className="me-2" />
                        Continuar
                      </>
                    )}
                  </Button>
                </div>
              </Form>
            </Card.Body>
          </Card>
        );

      case "instructions":
        return (
          <Card className="border-0 shadow-lg">
            <Card.Body className="p-5">
              {/* Header */}
              <div className="text-center mb-4">
                <div
                  className="mx-auto mb-3 rounded-circle d-flex align-items-center justify-content-center"
                  style={{
                    width: "70px",
                    height: "70px",
                    background:
                      "linear-gradient(135deg, #007bff 0%, #0056b3 100%)",
                    color: "white",
                    boxShadow: "0 6px 24px rgba(0, 123, 255, 0.3)",
                  }}
                >
                  <IconDeviceMobile size={35} />
                </div>

                <h3 className="fw-bold text-dark mb-2">Prepare seu WhatsApp</h3>
                <p className="text-muted mb-0">
                  Siga os passos abaixo ANTES de gerar o QR Code
                </p>
              </div>

              {/* Instruções */}
              <div className="mb-4">
                <Alert
                  variant="info"
                  className="border-0"
                  style={{ backgroundColor: "#e3f2fd" }}
                >
                  <div className="d-flex align-items-start gap-3">
                    <IconBrandWhatsapp
                      size={24}
                      style={{ color: "#25D366", marginTop: "2px" }}
                    />
                    <div>
                      <strong className="text-dark">
                        Número a ser conectado:
                      </strong>
                      <div
                        className="fw-bold"
                        style={{ color: "#25D366", fontSize: "16px" }}
                      >
                        {phoneNumber}
                      </div>
                    </div>
                  </div>
                </Alert>
              </div>

              {/* Passos */}
              <div className="mb-5">
                <h5 className="fw-bold text-dark mb-4">
                  📱 Passos obrigatórios:
                </h5>

                <div className="d-flex flex-column gap-3">
                  {[
                    {
                      step: "1",
                      title: "Abra o WhatsApp",
                      description:
                        "Abra o WhatsApp no celular do número " + phoneNumber,
                      color: "#25D366",
                    },
                    {
                      step: "2",
                      title: "Menu → Dispositivos conectados",
                      description:
                        "Toque nos 3 pontinhos → Dispositivos conectados",
                      color: "#128C7E",
                    },
                    {
                      step: "3",
                      title: "Conectar um dispositivo",
                      description:
                        'Toque em "Conectar um dispositivo" e deixe a câmera pronta',
                      color: "#075e54",
                    },
                  ].map((item, index) => (
                    <div
                      key={index}
                      className="d-flex align-items-start gap-3 p-3 rounded-3"
                      style={{
                        background: "#f8f9fa",
                        border: "1px solid #e9ecef",
                      }}
                    >
                      <div
                        className="d-flex align-items-center justify-content-center rounded-circle fw-bold text-white"
                        style={{
                          width: "30px",
                          height: "30px",
                          backgroundColor: item.color,
                          fontSize: "14px",
                          flexShrink: 0,
                        }}
                      >
                        {item.step}
                      </div>
                      <div>
                        <div className="fw-semibold text-dark">
                          {item.title}
                        </div>
                        <small className="text-muted">{item.description}</small>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Aviso importante */}
              <Alert variant="warning" className="mb-4">
                <div className="d-flex align-items-start gap-2">
                  <IconAlertTriangle size={20} style={{ marginTop: "2px" }} />
                  <div>
                    <strong>⚠️ IMPORTANTE:</strong>
                    <br />
                    • Só clique em "Gerar QR Code" DEPOIS de deixar a câmera do
                    WhatsApp pronta
                    <br />
                    • Você terá apenas 30 segundos para escanear o código
                    <br />• NÃO feche esta janela até ver a mensagem de sucesso
                  </div>
                </div>
              </Alert>

              {/* Erro */}
              {connectionError && (
                <Alert variant="danger" className="mb-4">
                  {connectionError}
                </Alert>
              )}

              {/* Botões */}
              <div className="d-flex gap-3">
                <Button
                  variant="outline-secondary"
                  size="lg"
                  onClick={handleBack}
                  disabled={isGeneratingQrCode}
                  className="flex-fill"
                  style={{
                    borderRadius: "12px",
                    fontWeight: "600",
                    padding: "16px",
                  }}
                >
                  <IconArrowLeft size={20} className="me-2" />
                  Voltar
                </Button>

                <Button
                  size="lg"
                  onClick={handleGenerateQrCode}
                  disabled={isGeneratingQrCode}
                  className="flex-fill"
                  style={{
                    background:
                      "linear-gradient(135deg, #25D366 0%, #128C7E 100%)",
                    border: "none",
                    borderRadius: "12px",
                    fontWeight: "600",
                    padding: "16px",
                  }}
                >
                  {isGeneratingQrCode ? (
                    <>
                      <Spinner animation="border" size="sm" className="me-2" />
                      Gerando...
                    </>
                  ) : (
                    <>
                      <IconQrcode size={20} className="me-2" />
                      Gerar QR Code
                    </>
                  )}
                </Button>
              </div>
            </Card.Body>
          </Card>
        );

      case "qr_scan":
        return (
          <Card className="border-0 shadow-lg">
            <Card.Header className="bg-transparent border-bottom-0 pb-0">
              <div className="d-flex align-items-center justify-content-between">
                <h5 className="mb-0 fw-bold">
                  <IconQrcode size={20} className="me-2" />
                  Escaneie o QR Code
                </h5>
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={handleBack}
                  style={{ borderRadius: "8px" }}
                >
                  <IconArrowLeft size={16} className="me-1" />
                  Voltar
                </Button>
              </div>
            </Card.Header>

            <Card.Body className="p-4">
              <div className="text-center mb-4">
                <Badge
                  bg="success"
                  className="mb-3 px-3 py-2"
                  style={{ fontSize: "12px" }}
                >
                  <IconBrandWhatsapp size={14} className="me-1" />
                  Número: {phoneNumber}
                </Badge>
              </div>

              {/* Countdown */}
              {countdown > 0 ? (
                <Alert variant="warning" className="mb-4 text-center">
                  <div className="d-flex align-items-center justify-content-center gap-2">
                    <IconClock size={18} />
                    <strong>Tempo restante: {countdown} segundos</strong>
                  </div>
                  <small className="d-block mt-1">
                    Escaneie rapidamente com a câmera do WhatsApp
                  </small>
                </Alert>
              ) : (
                isCheckingConnection && (
                  <Alert variant="info" className="mb-4 text-center">
                    <div className="d-flex align-items-center justify-content-center gap-2">
                      <Spinner animation="border" size="sm" />
                      <strong>Verificando conexão...</strong>
                    </div>
                    <small className="d-block mt-1">
                      Aguardando confirmação do WhatsApp
                    </small>
                  </Alert>
                )
              )}

              {/* QR Code */}
              {qrCodeData && (
                <div className="text-center mb-4">
                  <div
                    className="qr-code-container p-3 bg-white rounded shadow-sm d-inline-block"
                    style={{
                      border: "3px solid #25D366",
                      borderRadius: "16px",
                    }}
                  >
                    <img
                      src={qrCodeData.imageUrl}
                      alt="QR Code WhatsApp"
                      className="img-fluid"
                      style={{
                        maxWidth: "280px",
                        width: "100%",
                        borderRadius: "8px",
                      }}
                    />
                  </div>

                  <div className="mt-3">
                    <h6 className="text-success mb-2">
                      📱 Aponte a câmera do WhatsApp para o código acima
                    </h6>
                    <small className="text-muted">
                      O código expira em {countdown} segundos. NÃO feche esta
                      janela!
                    </small>
                  </div>
                </div>
              )}

              {connectionError && (
                <Alert variant="danger" className="mt-3">
                  <div className="d-flex align-items-center gap-2">
                    <IconAlertTriangle size={18} />
                    <div>
                      <strong>Erro:</strong> {connectionError}
                      <br />
                      <small>Clique em "Voltar" para tentar novamente</small>
                    </div>
                  </div>
                </Alert>
              )}

              {/* Instruções rápidas */}
              {!connectionError && qrCodeData && (
                <div
                  className="mt-4 p-3 rounded-3"
                  style={{ background: "#f8f9fa" }}
                >
                  <h6 className="fw-bold text-dark mb-2">
                    ✅ Lembra dos passos:
                  </h6>
                  <div className="d-flex flex-column gap-1">
                    <small>
                      1️⃣ WhatsApp → Menu (3 pontos) → Dispositivos conectados
                    </small>
                    <small>2️⃣ "Conectar um dispositivo"</small>
                    <small>3️⃣ Aponte a câmera para o QR Code acima</small>
                  </div>
                </div>
              )}
            </Card.Body>
          </Card>
        );

      case "completed":
        return (
          <Card className="border-0 shadow-lg">
            <Card.Body className="p-5 text-center">
              <div
                className="mx-auto mb-4 rounded-circle d-flex align-items-center justify-content-center"
                style={{
                  width: "100px",
                  height: "100px",
                  background:
                    "linear-gradient(135deg, #28a745 0%, #20c997 100%)",
                  color: "white",
                  boxShadow: "0 8px 32px rgba(40, 167, 69, 0.3)",
                }}
              >
                <IconCheck size={50} />
              </div>

              <h2 className="fw-bold text-success mb-3">Conexão Realizada!</h2>

              <p className="text-muted mb-4">
                O WhatsApp {phoneNumber} foi conectado com sucesso. Agora você
                pode usar o agente IA para responder mensagens automaticamente.
              </p>

              <div className="d-flex flex-column gap-3 mt-4">
                <Badge
                  bg="success"
                  className="d-flex align-items-center justify-content-center gap-2 px-3 py-3"
                  style={{ fontSize: "14px" }}
                >
                  <IconBrandWhatsapp size={20} />
                  WhatsApp Conectado: {phoneNumber}
                </Badge>
              </div>

              <Alert variant="success" className="mt-4">
                <div className="text-center">
                  <strong>🎉 Parabéns!</strong>
                  <br />
                  <small>
                    Seu WhatsApp está conectado e funcionando. O agente IA já
                    pode responder suas mensagens automaticamente.
                  </small>
                </div>
              </Alert>

              <div
                className="mt-5 pt-4"
                style={{ borderTop: "1px solid #e9ecef" }}
              >
                <small className="text-muted">
                  A conexão foi estabelecida com segurança. Você pode fechar
                  esta página.
                </small>
              </div>
            </Card.Body>
          </Card>
        );

      default:
        return null;
    }
  };

  return (
    <Container className="py-5">
      <Row className="justify-content-center">
        <Col lg={8} md={10} sm={12}>
          {renderContent()}
        </Col>
      </Row>
    </Container>
  );
};

export default WhatsAppConnectionFlow;
